const { QorpyApiInterface } = require('./providers')
const ElectronicInvoicingPgAdapter = require('./pg-adapter')
const ElectronicInvoicingEventEmitter = require('./event-emitter')
const get = require('lodash/get')
const keyBy = require('lodash/keyBy')
const { getMarketVat } = require('../../product/tools')
const chunk = require('lodash/chunk')
const { EncryptionService } = require('../../encryption-service')

class ElectronicInvoicingApi {
  constructor (pgConnection, quickbooksApi, isomorphicFetch, nrsApiConfigs = {}) {
    nrsApiConfigs = nrsApiConfigs || {}
    this.pgConnection = pgConnection
    this.quickbooksApi = quickbooksApi
    this.businessTin = nrsApiConfigs.businessTin
    this.businessName = nrsApiConfigs.businessName
    this.businessTelephone = nrsApiConfigs.businessTelephone
    this.businessEmail = nrsApiConfigs.businessEmail
    this.businessPostalCode = nrsApiConfigs.businessPostalCode
    this.businessStateCode = nrsApiConfigs.businessStateCode
    this.businessLGACode = nrsApiConfigs.businessLGACode
    this.businessCityName = nrsApiConfigs.businessCityName
    this.businessStreetName = nrsApiConfigs.businessStreetName
    this.businessDescription = nrsApiConfigs.businessDescription
    this.ngVatIncomeTaxAccountId = get(quickbooksApi, 'quickbooksInterface.accountIds.NG.vatIncomeTaxAccountId')
    this.qorpyApi = new QorpyApiInterface(
      isomorphicFetch,
      nrsApiConfigs.baseUrl,
      nrsApiConfigs.environment,
      nrsApiConfigs.businessApiKey,
      nrsApiConfigs.businessId
    )
    this.pgAdapter = new ElectronicInvoicingPgAdapter(pgConnection)
    this.eventEmitter = new ElectronicInvoicingEventEmitter(pgConnection)
    this.encryptionService = new EncryptionService(nrsApiConfigs.encryptionKey)
    this.PROVIDERS = {
      QORPY: 'qorpy'
    }
  }

  parseDocNumber (docNumber) {
    return `${docNumber}`.replace(/[^a-z0-9]/gi, '')
  }

  mapQorpyRawData (code, message, data) {
    return {
      response_code: code || '',
      response_message: message || '',
      data,
      invoice_number: get(data, 'invoiceNumber'),
      provider: this.PROVIDERS.QORPY
    }
  }

  calculateTotalTaxableAmount (taxPercentage, totalTaxAmount) {
    return (100 * totalTaxAmount) / (taxPercentage)
  }

  /**
   * @param {'QBO_ENTITY_TO_QORPY'} channel
  */
  getRequestMapperFunction (channel) {
    return {
      /**
       * @param {object} entity
       * @param {object} customer
       * @param {string|null} vatAccountId
       * @param {number} countryVat
       * @param {'invoice'|'creditmemo'} [txnType='invoice']
       * @param {{invoiceIrn: string, issueDate: string}[]} invoiceIrns
       * @returns {object}
       */
      QBO_ENTITY_TO_QORPY: (entity, customer, vatAccountId, countryVat, txnType = 'invoice', invoiceIrns = []) => {
        const invoicePayableAmount = get(entity, 'TotalAmt')
        const hasTotalTaxDetailAmount = get(entity, 'TxnTaxDetail.TotalTax', 0) > 0
        const qboInvoiceLines = get(entity, 'Line', [])
        const { invoiceLine, vatLines } = qboInvoiceLines.reduce((acc, line) => {
          const lineItemName = get(line, 'SalesItemLineDetail.ItemRef.name')
          const lineDetailType = get(line, 'DetailType')
          const isVatLineAccountId = get(line, 'SalesItemLineDetail.ItemAccountRef.value') === vatAccountId
          const hasTaxCodeRef = get(line, 'SalesItemLineDetail.TaxCodeRef.value') === 'TAX'
          const hasTaxClassificationRef = !!get(line, 'SalesItemLineDetail.TaxClassificationRef.value')
          const isTotalVatLineItem = `${lineItemName}`.startsWith('Total VAT (')
          if (lineDetailType === 'SubTotalLineDetail') {
            return acc
          }

          if ((isVatLineAccountId || hasTaxCodeRef || hasTaxClassificationRef || isTotalVatLineItem) && !hasTotalTaxDetailAmount) {
            const match = lineItemName.match(/\(([\d.]+)%\)/)
            const vatPercent = match ? parseFloat(match[1]) : countryVat
            acc.vatLines.push({
              percentage: vatPercent,
              amount: get(line, 'Amount')
            })

            // Never include custom VATABLE lines in the invoice lines sent to the NRS
            return acc
          }

          acc.invoiceLine.push({
            invoiceLineProductCategory: this.qorpyApi.DEFAULT_PRODUCT_CATEGORY,
            invoiceLineHsnCode: this.qorpyApi.DEFAULT_HS_CODE,
            invoiceLineItemName: lineItemName,
            invoiceLineItemDescription: get(line, 'Description'),
            invoiceLineItemSellersItemIdentification: get(line, 'SalesItemLineDetail.ItemRef.name'),
            invoiceLineInvoicedQuantity: get(line, 'SalesItemLineDetail.Qty'),
            invoiceLineExtensionAmount: get(line, 'Amount'),
            invoiceLinePriceAmount: get(line, 'SalesItemLineDetail.UnitPrice'),
            invoiceLinePriceBaseQuantity: 1,
            // reference => EA for each
            invoiceLinePriceUnit: 'EA',
            invoiceLineDiscountRate: 0,
            invoiceLineDiscountAmount: 0,
            invoiceLineFeeRate: 0,
            invoiceLineFeeAmount: 0
          })
          return acc
        }, { invoiceLine: [], vatLines: [] })

        const invoiceTaxTotal = []
        if (hasTotalTaxDetailAmount) {
          invoiceTaxTotal.push({
            invoiceTaxTotalAmount: get(entity, 'TxnTaxDetail.TotalTax'),
            invoiceTaxTotalSubTotal: [{
              taxSubTotalCategoryPercent: get(entity, 'TxnTaxDetail.TaxLine.0.TaxLineDetail.TaxPercent'),
              taxSubTotalCategoryId: this.qorpyApi.TAX_CATEGORIES.STANDARD_VAT,
              taxSubTotalTaxAmount: get(entity, 'TxnTaxDetail.TotalTax'),
              taxSubTotalTaxableAmount: get(entity, 'TxnTaxDetail.TaxLine.0.TaxLineDetail.NetAmountTaxable')
            }]
          })
        } else {
          if (vatLines.length > 0) {
            const totalVatAmount = vatLines.reduce((a, b) => a + b.amount, 0)
            const vatPercentage = vatLines[0].percentage
            invoiceTaxTotal.push({
              invoiceTaxTotalAmount: totalVatAmount,
              invoiceTaxTotalSubTotal: [{
                taxSubTotalCategoryPercent: vatPercentage,
                taxSubTotalCategoryId: totalVatAmount > 0 ? this.qorpyApi.TAX_CATEGORIES.STANDARD_VAT : this.qorpyApi.TAX_CATEGORIES.ZERO_VAT,
                taxSubTotalTaxAmount: totalVatAmount,
                taxSubTotalTaxableAmount: this.calculateTotalTaxableAmount(vatPercentage, totalVatAmount)
              }]
            })
          } else {
            // API needs a default tax_total amount, cant be left empty
            invoiceTaxTotal.push({
              invoiceTaxTotalAmount: 0,
              invoiceTaxTotalSubTotal: [
                {
                  taxSubTotalCategoryPercent: 0,
                  taxSubTotalCategoryId: this.qorpyApi.TAX_CATEGORIES.ZERO_VAT,
                  taxSubTotalTaxAmount: 0,
                  taxSubTotalTaxableAmount: 0
                }
              ]
            })
          }
        }

        const totalTaxAmount = invoiceTaxTotal.reduce((a, b) => a + b.invoiceTaxTotalAmount, 0)

        return {
          requestType: !customer.taxIdNumber
            ? this.qorpyApi.REQUEST_TYPES.B2C
            : customer.isValidFIRSTIN
              ? this.qorpyApi.REQUEST_TYPES.B2B
              : this.qorpyApi.REQUEST_TYPES.B2C,
          invoiceIssueDate: get(entity, 'TxnDate'),
          invoiceDueDate: get(entity, 'DueDate'),
          invoiceNumber: this.parseDocNumber(get(entity, 'DocNumber')),
          invoiceNote: get(entity, 'DocNumber'),
          invoicePaymentStatus: this.qorpyApi.PAYMENT_STATUS.PAID,
          invoiceTypeCode: txnType === 'invoice'
            ? this.qorpyApi.INVOICE_TYPE_CODES.COMMERCIAL_INVOICE
            : this.qorpyApi.INVOICE_TYPE_CODES.CREDIT_NOTE,
          invoiceBusinessId: this.qorpyApi.businessId,
          invoiceDocumentCurrencyCode: this.qorpyApi.CURRENCY_CODES.NGN,
          invoiceTaxCurrencyCode: this.qorpyApi.CURRENCY_CODES.NGN,

          // QBO Field information
          invoiceSupplierStreetName: this.businessStreetName,
          invoiceSupplierCityName: this.businessCityName,
          invoiceSupplierLga: this.businessLGACode,
          invoiceSupplierState: this.businessStateCode,
          invoiceSupplierCountry: this.qorpyApi.COUNTRY_CODES.NG,
          invoiceSupplierPostalZone: this.businessPostalCode,
          invoiceSupplierPartyBusinessDescription: this.businessDescription,
          invoiceSupplierPartyTin: this.businessTin,
          invoiceSupplierPartyEmail: this.businessEmail,
          invoiceSupplierPartyTelephone: this.businessTelephone,
          invoiceSupplierPartyName: this.businessName,

          // QBO Customer information only gets appended for customers with valid FIRS TIN numbers
          ...(customer.isValidFIRSTIN
            ? {
              invoiceCustomerStreetName: get(entity, 'ShipAddr.Line1'),
              invoiceCustomerCityName: get(entity, 'ShipAddr.City'),
              invoiceCustomerLga: customer.lga || this.businessLGACode,
              invoiceCustomerState: customer.state || this.businessStateCode,
              invoiceCustomerPartyTelephone: customer.phoneNumber,
              invoiceCustomerCountry: this.qorpyApi.COUNTRY_CODES.NG,
              invoiceCustomerPostalZone: '',
              invoiceCustomerPartyBusinessDescription: '',
              invoiceCustomerPartyTin: get(customer, 'taxIdNumber'),
              invoiceCustomerPartyEmail: get(entity, 'BillEmail.Address'),
              invoiceCustomerPartyName: get(entity, 'CustomerRef.name')
            }
            : {}),

          // amounts
          invoiceLineExtensionAmount: invoicePayableAmount - totalTaxAmount,
          invoiceTaxInclusiveAmount: invoicePayableAmount,
          invoiceTaxExclusiveAmount: invoicePayableAmount - totalTaxAmount,
          invoicePayableAmount,
          invoiceLine,
          invoiceTaxTotal,

          // Credit Memo special filed linking it back to invoice transaction
          ...(txnType === 'creditmemo'
            ? { invoiceBillingReference: Array.isArray(invoiceIrns)
              ? invoiceIrns.map(i => ({ invoiceDocumentReferenceIrn: i.invoiceIrn, invoiceDocumentReferenceIssueDate: i.issueDate }))
              : []
            }
            : {}
          )
        }
      }
    }[channel]
  }

  /**
   * @param {'qorpy'} channel
  */
  getCustomerTINValidatorFunction (channel) {
    return {
      qorpy: async (taxIdNumber) => {
        const response = await this.qorpyApi.getFIRSTin(taxIdNumber)
        return this.qorpyApi.RESPONSE_CODES[response.responseCode] === 'success'
      }
    }[channel]
  }

  /**
   * @param {'qbo'} origin
  **/
  getInvoiceGroupingFunction (origin) {
    return {
      qbo: (qboInvoices, companyCode, lrAccountsByInvoiceTxnId, qboLoanAccountsById) => {
        const { paidInvoices, unpaidInvoices, linkedPaidInvoiceIds, invoiceLoanAccountNotFound } = qboInvoices.reduce((acc, invoice) => {
          const txnId = get(invoice, 'Id')
          const docNumber = get(invoice, 'DocNumber')
          const invoiceLoanAccount = lrAccountsByInvoiceTxnId[txnId]

          // If the invoice has a Loan account, payment status would be determined off of the Loans outstanding balance
          if (invoiceLoanAccount) {
            const { loanAccountId, linkedInvoicesTxnIds } = invoiceLoanAccount
            const qboLoanAccount = qboLoanAccountsById[loanAccountId]
            // Means the account is found in Quickbooks, inactive accounts for example do not show up on the API
            if (qboLoanAccount) {
              if (qboLoanAccount.CurrentBalance > 0) {
                acc.unpaidInvoices.push({...invoice, LoanAccount: qboLoanAccount})
              } else {
                acc.paidInvoices.push({...invoice, LoanAccount: qboLoanAccount})
                linkedInvoicesTxnIds.forEach(id => {
                  if (`${id}` !== `${txnId}`) {
                    acc.linkedPaidInvoiceIds.add(id)
                  }
                })
              }
            } else {
              acc.invoiceLoanAccountNotFound.push({ txnId, companyCode, loanAccountId, docNumber })
            }
          } else {
            // We rely on the Balance status in the invoice to determine payment status
            if (invoice.Balance > 0) {
              acc.unpaidInvoices.push({...invoice, LoanAccount: null})
            } else {
              acc.paidInvoices.push({...invoice, LoanAccount: null})
            }
          }
          return acc
        }, { paidInvoices: [], unpaidInvoices: [], linkedPaidInvoiceIds: new Set(), invoiceLoanAccountNotFound: [] })

        return {
          paidInvoices,
          unpaidInvoices, // remember to use var
          linkedPaidInvoiceIds: [...linkedPaidInvoiceIds],
          invoiceLoanAccountNotFound // remember to use var
        }
      }
    }[origin]
  }

  /**
   * @param {'qorpy'} channel
  **/
  getResponseToTableMappingFunction (channel) {
    return {
      /**
       * @param {PromiseSettledResult<any>[]} response
      */
      qorpy: (response) => {
        return response.reduce((acc, res) => {
          if (res.status === 'fulfilled') {
            const { value: { responseCode, responseMessage, data } } = res
            // We got data from the NRS with suceess code
            if (data && this.qorpyApi.RESPONSE_CODES[responseCode] === 'success') {
              acc.success.push(this.mapQorpyRawData(responseCode, responseMessage, data))
            } else {
              acc.failed.push(res.value)
            }
          }

          if (res.status === 'rejected') {
            acc.failed.push(res)
          }
          return acc
        }, { success: [], failed: [] }) // remember to use failed var
      }
    }[channel]
  }

  /**
   * @param {'qbo'} origin
   * @param {'KE'|'NG'} companyCode
   * @param {{id: number}[]} transactions
  */
  async transmitInvoices (origin, companyCode, transactions, { crypto } = {}) {
    if (origin !== 'qbo') {
      throw new Error(`Unsupported accounting origin: '${origin}'`)
    }

    if (!this.businessTin) {
      throw new Error(`Missing business TIN from configured NRS API configs`)
    }

    const eInvoiceRows = []
    const vatAccountId = companyCode === 'NG' ? this.ngVatIncomeTaxAccountId : null
    // Have this as the defauct country vat if we can't infer vat value by looking at invoice line alone
    const { vat: countryVat } = getMarketVat(`${companyCode}`.toLowerCase())
    const txnIds = transactions.map(t => t.id)

    // Fetch latest state of invoice from Quickbooks before transmitting to Tax authority
    const qboInvoices = await this.quickbooksApi.entities.invoice.getList(txnIds, companyCode)
    const { customerIds, invoiceTxnIds } = qboInvoices.reduce((acc, val) => {
      acc.customerIds.push(get(val, 'CustomerRef.value'))
      acc.invoiceTxnIds.push(get(val, 'Id'))
      return acc
    }, { customerIds: [], invoiceTxnIds: [] })

    const [customers, invoicesLRAccounts] = await Promise.all([
      this.pgAdapter.getQboCustomerDetail(customerIds, companyCode),
      this.pgAdapter.getInvoiceLoanAccount(invoiceTxnIds, companyCode)
    ])

    // Decrypt the taxIdNumber for each customer
    customers.forEach(c => {
      try {
        c.taxIdNumber = this.encryptionService.decrypt(c.taxIdNumberEncrypted, crypto)
      } catch (error) {
        console.warn(`Failed to decrypt taxIdNumber for customer ${c.customerId}. Error: ${error.message}`)
      }
    })
    const customerByQboId = keyBy(customers, 'customerId')
    const lrAccountsByInvoiceTxnId = keyBy(invoicesLRAccounts, 'invoiceTxnId')
    let qboLoanAccounts = []
    if (invoicesLRAccounts.length > 0) {
      const loanAccountIds = invoicesLRAccounts.map(a => a.loanAccountId)
      const accounts = await this.quickbooksApi.entities.account.getList(loanAccountIds, companyCode)
      qboLoanAccounts.push(...accounts)
    }

    const qboLoanAccountsById = keyBy(qboLoanAccounts, 'Id')
    const groupingFunction = this.getInvoiceGroupingFunction(origin)
    const { paidInvoices, linkedPaidInvoiceIds } = groupingFunction(qboInvoices, companyCode, lrAccountsByInvoiceTxnId, qboLoanAccountsById)

    // Fetch invoices that were linked prior to a fellow paid invoice via a journal entry
    if (linkedPaidInvoiceIds.length > 0) {
      const linkedQboInvoices = await this.quickbooksApi.entities.invoice.getList(linkedPaidInvoiceIds, companyCode)
      paidInvoices.push(...linkedQboInvoices)
    }

    if (companyCode === 'NG' && paidInvoices.length > 0) {
      const taxIdValidatorFunction = this.getCustomerTINValidatorFunction('qorpy')
      await Promise.all(customers.map(async customer => {
        try {
          if (customer.taxIdNumber) {
            const isValidFIRSTIN = await taxIdValidatorFunction(customer.taxIdNumber)
            customer.isValidFIRSTIN = isValidFIRSTIN
          }
        } catch (error) {
          console.warn(`Failed to validate FIRSTin for customer ${customer.customerId}. Error: ${error.message}`)
        }
      }))

      const requestMapperFunction = this.getRequestMapperFunction('QBO_ENTITY_TO_QORPY')
      const requestProps = paidInvoices.map(invoice => {
        const customerId = get(invoice, 'CustomerRef.value')
        const customer = customerByQboId[customerId] || {}
        return requestMapperFunction(invoice, customer, vatAccountId, countryVat, 'invoice')
      })

      const responseToTableMapperFunction = this.getResponseToTableMappingFunction('qorpy')
      const batchRequests = chunk(requestProps.map(data => this.qorpyApi.createInvoice(data)), this.qorpyApi.BATCH_REQUEST_SIZE)
      for (const requests of batchRequests) {
        const response = await Promise.allSettled(requests)
        const { success } = responseToTableMapperFunction(response)
        if (success.length > 0) {
          const rows = await this.pgAdapter.upsert(success, companyCode)
          eInvoiceRows.push(...rows)
        }
      }
    }

    return { transmittedRows: eInvoiceRows }
  }

  /**
   * @param {'qbo'} origin
   * @param {'KE'|'NG'} companyCode
   * @param {{id: number, invoiceIrns: {invoiceIrn: string, issueDate: string}[]}[]} transactions
  */
  async transmitCreditNotes (origin, companyCode, transactions, { crypto } = {}) {
    if (origin !== 'qbo') {
      throw new Error(`Unsupported accounting origin: '${origin}'`)
    }

    if (!this.businessTin) {
      throw new Error(`Missing business TIN from configured NRS API configs`)
    }

    const eInvoiceRows = []
    const vatAccountId = companyCode === 'NG' ? this.ngVatIncomeTaxAccountId : null
    // Have this as the defauct country vat if we can't infer vat value by looking at invoice line alone
    const { vat: countryVat } = getMarketVat(`${companyCode}`.toLowerCase())
    const txnIds = transactions.map(t => t.id)
    const transactionsById = keyBy(transactions, 'id')

    // Fetch latest state of credit memos from Quickbooks before transmitting to Tax authority
    const creditMemos = await this.quickbooksApi.entities.creditMemo.getList(txnIds, companyCode)
    const { customerIds } = creditMemos.reduce((acc, val) => {
      acc.customerIds.push(get(val, 'CustomerRef.value'))
      return acc
    }, { customerIds: [] })

    const customers = await this.pgAdapter.getQboCustomerDetail(customerIds, companyCode)
    // Decrypt the taxIdNumber for each customer
    customers.forEach(c => {
      try {
        c.taxIdNumber = this.encryptionService.decrypt(c.taxIdNumberEncrypted, crypto)
      } catch (error) {
        console.warn(`Failed to decrypt taxIdNumber for customer ${c.customerId}. Error: ${error.message}`)
      }
    })

    const customerByQboId = keyBy(customers, 'customerId')
    const { fullyAppliedCreditMemos } = creditMemos.reduce((acc, val) => {
      if (val.TotalAmt > 0 && val.RemainingCredit <= 0) {
        acc.fullyAppliedCreditMemos.push(val)
      } else {
        acc.partiallyAppliedCreditMemos.push(val)
      }
      return acc
    }, { fullyAppliedCreditMemos: [], partiallyAppliedCreditMemos: [] }) // remember to use partiallyAppliedCreditMemos

    // We only transmit credit memos that have been fully applied to respective transmitted invoices
    if (companyCode === 'NG' && fullyAppliedCreditMemos.length > 0) {
      const taxIdValidatorFunction = this.getCustomerTINValidatorFunction('qorpy')
      await Promise.all(customers.map(async customer => {
        try {
          if (customer.taxIdNumber) {
            const isValidFIRSTIN = await taxIdValidatorFunction(customer.taxIdNumber)
            customer.isValidFIRSTIN = isValidFIRSTIN
          }
        } catch (error) {
          console.warn(`Failed to validate FIRSTin for customer ${customer.customerId}. Error: ${error.message}`)
        }
      }))

      const requestMapperFunction = this.getRequestMapperFunction('QBO_ENTITY_TO_QORPY')
      const requestProps = fullyAppliedCreditMemos.map(creditMemo => {
        const customerId = get(creditMemo, 'CustomerRef.value')
        const customer = customerByQboId[customerId] || {}
        return requestMapperFunction(creditMemo, customer, vatAccountId, countryVat, 'creditmemo', transactionsById[creditMemo.Id].invoiceIrns)
      })

      const responseToTableMapperFunction = this.getResponseToTableMappingFunction('qorpy')
      const batchRequests = chunk(requestProps.map(data => this.qorpyApi.createInvoice(data)), this.qorpyApi.BATCH_REQUEST_SIZE)
      for (const requests of batchRequests) {
        const response = await Promise.allSettled(requests)
        const { success } = responseToTableMapperFunction(response)
        if (success.length > 0) {
          const rows = await this.pgAdapter.upsert(success, companyCode)
          eInvoiceRows.push(...rows)
        }
      }
    }
    return { transmittedRows: eInvoiceRows }
  }

  /**
   * @param {'qbo'} origin
   * @param {'KE'|'NG'} companyCode
   * @param {{id: number, invoiceIrn: string, txnType: 'creditmemo'|'invoice'}[]} transactions
  */
  async reverseTransactions (origin, companyCode, transactions) {
    if (origin !== 'qbo') {
      throw new Error(`Unsupported accounting origin: '${origin}'`)
    }

    const eInvoiceRows = []
    const { creditMemos, invoices } = transactions.reduce((acc, val) => {
      if (val.txnType === 'creditmemo') {
        acc.creditMemos.push(val)
      }

      if (val.txnType === 'invoice') {
        acc.invoices.push(val)
      }
      return acc
    }, { creditMemos: [], invoices: [] })

    const creditMemosByTxnId = keyBy(creditMemos, 'id')
    const invoicesByTxnId = keyBy(invoices, 'id')

    const [qboInvoices, qboCreditMemos] = await Promise.all([
      this.quickbooksApi.entities.invoice.getList(Object.keys(invoicesByTxnId), companyCode),
      this.quickbooksApi.entities.creditMemo.getList(Object.keys(creditMemosByTxnId), companyCode)
    ])

    const voidedCreditMemoIrns = qboCreditMemos
      // Return Credit Memos that have an existing invoiceIrn and where the Credit Memo has actually been voided
      .filter(c => (creditMemosByTxnId[c.Id].invoiceIrn && c.RemainingCredit === 0 && c.TotalAmt === 0))
      .map(c => (creditMemosByTxnId[c.Id].invoiceIrn))
    const voidedInvoicesIrns = qboInvoices
      // Return Invoices that have an existing invoiceIrn and where the Invoice has actually been voided
      .filter(i => (invoicesByTxnId[i.Id].invoiceIrn && i.Balance === 0 && i.TotalAmt === 0))
      .map(i => (invoicesByTxnId[i.Id].invoiceIrn))

    const requestProps = [...voidedCreditMemoIrns, ...voidedInvoicesIrns]
    if (companyCode === 'NG' && requestProps.length > 0) {
      const batchRequests = chunk(requestProps.map(data => this.qorpyApi.reverseInvoice(data)), this.qorpyApi.BATCH_REQUEST_SIZE)

      for (const requests of batchRequests) {
        const response = await Promise.allSettled(requests)
        const creditNotesIrns = response.reduce((acc, val) => {
          const { value } = val
          if (val.status === 'fulfilled' && this.qorpyApi.RESPONSE_CODES[value.responseCode] === 'success' && value.invoiceIrn) {
            acc.push(value.invoiceIrn)
          }
          return acc
        }, [])
        // Only map data when a credit note irn has been returned from API
        if (creditNotesIrns.length > 0) {
          // This defaults to returning the last 50 credit notes created on the integration
          // To keep this light we want only the last 10 credit note results created in API.
          // This ideally returns all the credit notes created above as a result of transaction reversal
          const seenInvoiceNumbers = new Set()
          const { data: creditNoteData, responseCode, responseMessage } = await this.qorpyApi.getCreditNotes(1, 10)
          const upsertRows = creditNoteData.map(d => this.mapQorpyRawData(responseCode, responseMessage, d)).filter(r => {
            if (seenInvoiceNumbers.has(r.invoice_number)) return false
            seenInvoiceNumbers.add(r.invoice_number)
            return true
          })
          if (upsertRows.length > 0) {
            const rows = await this.pgAdapter.upsert(upsertRows, companyCode)
            eInvoiceRows.push(...rows)
          }
        }
      }
    }

    return { transmittedRows: eInvoiceRows }
  }

  async updateInvoicePaymentStatus (origin, companyCode, txnIds) {}
}

module.exports = ElectronicInvoicingApi
